import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useBranchContext } from "@/contexts/BranchContext";
import { useBranches } from "@/hooks/useBranches";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Package, Download, ShoppingCart, Pencil, Plus } from "lucide-react";
import { NoMatchingRecords } from "@/components/NoMatchingRecords";
import { getTodayInZambia, getZambiaDayBounds, formatInZambia } from "@/lib/zambia-time";
import { useEmployeeViewSettings } from "@/hooks/useEmployeeViewSettings";
import { useEmployeeNotificationPrefs } from "@/hooks/useEmployeeNotificationPrefs";
import { ActivityTimeline } from "@/components/ActivityTimeline";
import { buildActivityEntries, getRangeQueryBounds, summarizeActivity, type ActivityEntry } from "@/lib/activity";

const ZAMBIA_OFFSET_MS = 2 * 60 * 60 * 1000;

function getZambiaNow(): Date {
  return new Date(Date.now() + ZAMBIA_OFFSET_MS);
}

function getGreeting(): "morning" | "afternoon" | "evening" {
  const hour = (new Date().getUTCHours() + 2) % 24;
  if (hour < 12) return "morning";
  if (hour < 17) return "afternoon";
  return "evening";
}

function formatZambiaDateTime(date: Date): string {
  const y = date.getUTCFullYear();
  const d = String(date.getUTCDate()).padStart(2, "0");
  const h = String(date.getUTCHours()).padStart(2, "0");
  const min = String(date.getUTCMinutes()).padStart(2, "0");
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${h}:${min} CAT - ${days[date.getUTCDay()]}, ${months[date.getUTCMonth()]} ${d}, ${y}`;
}

function getFirstName(fullName: string | null | undefined): string {
  if (!fullName?.trim()) return "";
  return fullName.trim().split(/\s+/)[0] || "";
}

/** Employee-safe: item_name, category, selling_price, stock_level, min_threshold only. No buying_price. */
export interface StockCheckItem {
  id: string;
  name: string;
  category: string | null;
  selling_price: number;
  current_stock: number;
  min_threshold: number;
  branch_id?: string | null;
}

/** Shape passed to Manual Entry when "Record Sale" is clicked from a row */
export interface PreselectedItemForSale {
  id: string;
  name: string;
  selling_price: number;
  current_stock: number;
}

export default function EmployeeStockView() {
  const { user, businessId, profile } = useAuth();
  const { selectedBranchId } = useBranchContext();
  const { branches } = useBranches();
  const { settings: employeeViewSettings } = useEmployeeViewSettings();
  const { prefs: notificationPrefs } = useEmployeeNotificationPrefs();
  const firstName = getFirstName(profile?.full_name ?? undefined);
  const greeting = getGreeting();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const [inventory, setInventory] = useState<StockCheckItem[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addSku, setAddSku] = useState("");
  const [addCategory, setAddCategory] = useState("");
  const [addSellingPrice, setAddSellingPrice] = useState("");
  const [addStock, setAddStock] = useState(0);
  const [addThreshold, setAddThreshold] = useState(5);
  const [addBranchId, setAddBranchId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => formatZambiaDateTime(getZambiaNow()));
  const [todaySummary, setTodaySummary] = useState({ salesCount: 0, salesTotal: 0, expenseCount: 0 });
  const [recentActivity, setRecentActivity] = useState<ActivityEntry[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [editItem, setEditItem] = useState<StockCheckItem | null>(null);
  const [editStock, setEditStock] = useState(0);
  const [editThreshold, setEditThreshold] = useState(0);
  const [saving, setSaving] = useState(false);

  const effectiveBizId = businessId ?? profile?.business_id ?? null;
  const fetchInventory = useCallback(() => {
    if (!effectiveBizId) return;
    let baseQuery = supabase
      .from("inventory")
      .select("id, name, category, selling_price, current_stock, min_threshold, branch_id")
      .order("name")
      .eq("business_id", effectiveBizId);
    // Include legacy rows with no branch so employees see same stock as admin when filtering by location
    if (selectedBranchId !== "all") {
      baseQuery = baseQuery.or(`branch_id.eq.${selectedBranchId},branch_id.is.null`);
    }
    baseQuery.then(({ data }) => {
      if (data) setInventory(data as StockCheckItem[]);
    });
  }, [effectiveBizId, selectedBranchId]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(formatZambiaDateTime(getZambiaNow())), 60_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (searchParams.get("openAdd") !== "1" || !employeeViewSettings.allowEmployeeAddInventoryItems) return;
    setAddOpen(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("openAdd");
      return next;
    }, { replace: true });
  }, [employeeViewSettings.allowEmployeeAddInventoryItems, searchParams, setSearchParams]);

  useEffect(() => {
    if (!user || !effectiveBizId) return;
    let cancelled = false;
    const fetchActivity = async () => {
      setLoadingActivity(true);
      const todayBounds = getRangeQueryBounds("today");

      const todaySalesQuery = supabase
        .from("sales")
        .select("id, item_name, quantity, final_amount, total_price, created_at, sold_by, voided_at")
        .eq("sold_by", user.id)
        .eq("business_id", effectiveBizId)
        .is("voided_at", null)
        .gte("created_at", todayBounds.startIso)
        .lte("created_at", todayBounds.endIso)
        .order("created_at", { ascending: false });
      const todayExpensesQuery = supabase
        .from("expenses")
        .select("id, category, description, amount, expense_date, date, created_by")
        .eq("created_by", user.id)
        .eq("business_id", effectiveBizId)
        .gte("expense_date", todayBounds.startDate)
        .lte("expense_date", todayBounds.endDate)
        .order("date", { ascending: false });

      try {
        const [todaySalesRes, todayExpensesRes] = await Promise.all([
          todaySalesQuery,
          todayExpensesQuery,
        ]);
        if (cancelled) return;
        const todayEntries = buildActivityEntries(todaySalesRes.data ?? [], todayExpensesRes.data ?? []);
        setTodaySummary(summarizeActivity(todayEntries));
        setRecentActivity(todayEntries.slice(0, 5));
      } catch (e) {
        if (cancelled || (e instanceof Error && e.name === "AbortError")) return;
        throw e;
      } finally {
        if (!cancelled) setLoadingActivity(false);
      }
    };

    fetchActivity();
    return () => { cancelled = true; };
  }, [effectiveBizId, user]);

  const filteredInventory = useMemo(() => {
    if (selectedBranchId === "all") return inventory;
    return inventory.filter((item) => (item as { branch_id?: string | null }).branch_id === selectedBranchId);
  }, [inventory, selectedBranchId]);

  const lowStockCount = useMemo(() => {
    return filteredInventory.filter((item) => item.current_stock <= (item.min_threshold ?? 0)).length;
  }, [filteredInventory]);

  const openEdit = (item: StockCheckItem) => {
    setEditItem(item);
    setEditStock(item.current_stock);
    setEditThreshold(item.min_threshold ?? 0);
  };

  const handleSaveStock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editItem) return;
    setSaving(true);
    const { error } = await supabase
      .from("inventory")
      .update({ current_stock: editStock, min_threshold: editThreshold })
      .eq("id", editItem.id);
    setSaving(false);
    setEditItem(null);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Stock updated", description: "Changes are logged for your manager." });
      fetchInventory();
    }
  };

  const targetBusinessId = effectiveBizId;

  const handleAddStock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetBusinessId) {
      toast({ title: "No business", description: "Your account must be linked to a business to add stock.", variant: "destructive" });
      return;
    }
    if (!addName?.trim()) {
      toast({ title: "Error", description: "Item name is required.", variant: "destructive" });
      return;
    }
    if (branches.length > 1 && !addBranchId) {
      toast({ title: "Select location", description: "Choose the location where this stock item belongs.", variant: "destructive" });
      return;
    }
    const sku = addSku?.trim() || `SKU-${Date.now()}`;
    setAdding(true);
    const { data, error } = await supabase
      .from("inventory")
      .insert({
        name: addName.trim(),
        sku,
        category: addCategory?.trim() || null,
        selling_price: parseFloat(addSellingPrice) || 0,
        current_stock: addStock,
        min_threshold: addThreshold,
        business_id: targetBusinessId,
        branch_id: addBranchId,
      })
      .select("id, name, category, selling_price, current_stock, min_threshold, branch_id")
      .single();
    setAdding(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Stock item added" });
      setAddName("");
      setAddSku("");
      setAddCategory("");
      setAddSellingPrice("");
      setAddStock(0);
      setAddThreshold(5);
      setAddBranchId(selectedBranchId !== "all" ? selectedBranchId : branches[0]?.id ?? null);
      setAddOpen(false);
      if (data) setInventory((prev) => [...prev, data as StockCheckItem].sort((a, b) => a.name.localeCompare(b.name)));
    }
  };

  return (
    <div data-testid="dashboard-main" className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0 w-full space-y-1 sm:flex-1">
          <h2 className="text-2xl font-display font-bold break-words">
            Good {greeting}{firstName ? `, ${firstName}` : ""}
          </h2>
          <p className="text-sm text-muted-foreground">{currentTime}</p>
          <p className="text-sm text-muted-foreground max-w-prose">
            Monitor your sales and inventory activity within your workspace.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
          {employeeViewSettings.allowEmployeeAddInventoryItems && (
            <Dialog
              open={addOpen}
              onOpenChange={(open) => {
                setAddOpen(open);
                if (open && !addBranchId) {
                  setAddBranchId(selectedBranchId !== "all" ? selectedBranchId : branches[0]?.id ?? null);
                }
                if (!open) setAddBranchId(selectedBranchId !== "all" ? selectedBranchId : branches[0]?.id ?? null);
              }}
            >
              <DialogTrigger asChild>
                <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Stock Item</DialogTitle>
                  <p className="text-sm text-muted-foreground">Add a new item to the inventory. Your manager will see it.</p>
                </DialogHeader>
                <form onSubmit={handleAddStock} className="space-y-4">
                  {branches.length > 0 && (
                    <div>
                      <Label>Location</Label>
                      <Select value={addBranchId ?? ""} onValueChange={(value) => setAddBranchId(value || null)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent>
                          {branches.map((branch) => (
                            <SelectItem key={branch.id} value={branch.id}>
                              {branch.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div><Label>Item name *</Label><Input value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="e.g. Tiles" required /></div>
                  <div><Label>SKU (optional)</Label><Input value={addSku} onChange={(e) => setAddSku(e.target.value)} placeholder="Leave blank for auto-generated" /></div>
                  <div><Label>Category (optional)</Label><Input value={addCategory} onChange={(e) => setAddCategory(e.target.value)} placeholder="e.g. Wall" /></div>
                  <div><Label>Selling price (ZMW)</Label><Input type="number" step="0.01" min="0" value={addSellingPrice} onChange={(e) => setAddSellingPrice(e.target.value)} placeholder="0" /></div>
                  <div><Label>Current stock</Label><Input type="number" min="0" value={addStock} onChange={(e) => setAddStock(Number(e.target.value) || 0)} /></div>
                  <div><Label>Low stock threshold</Label><Input type="number" min="0" value={addThreshold} onChange={(e) => setAddThreshold(Number(e.target.value) || 0)} /></div>
                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
                    <Button type="submit" disabled={adding}>{adding ? "Adding…" : "Add Item"}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
          {employeeViewSettings.allowEmployeeExportInventory && filteredInventory.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const header = "Item Name,Category,Selling Price,Stock\n";
              const rows = filteredInventory
                .map((i) => `"${i.name}","${i.category || ""}",${i.selling_price},${i.current_stock}`)
                .join("\n");
              const blob = new Blob([header + rows], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = "stock-check.csv";
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            <Download className="h-4 w-4 mr-1" />
            Export CSV
          </Button>
          )}
        </div>
      </div>

      <Card className="border border-border">
        <CardHeader className="space-y-2">
          <CardTitle className="text-base">Today So Far</CardTitle>
          <p className="text-sm text-muted-foreground">
            Your current shift snapshot and the stock that needs your attention right now.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className="rounded-md border border-border px-4 py-4">
              <div className="text-sm text-muted-foreground">Sales logged</div>
              <div className="text-2xl font-bold">{todaySummary.salesCount}</div>
            </div>
            <div className="rounded-md border border-border px-4 py-4">
              <div className="text-sm text-muted-foreground">Sales total</div>
              <div className="text-2xl font-bold">K {todaySummary.salesTotal.toLocaleString()}</div>
            </div>
            <div className="rounded-md border border-border px-4 py-4">
              <div className="text-sm text-muted-foreground">Expenses logged</div>
              <div className="text-2xl font-bold">{todaySummary.expenseCount}</div>
            </div>
            <div className="rounded-md border border-border px-4 py-4">
              <div className="text-sm text-muted-foreground">Low stock items</div>
              <div className="text-2xl font-bold">{lowStockCount}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-base">Latest From Your Shift</CardTitle>
            <p className="text-sm text-muted-foreground">
              A short preview of today&apos;s latest actions. Use My Activity to browse day, week, month, and all-time history.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate("/my-activity")}>
            View Full Activity
          </Button>
        </CardHeader>
        <CardContent>
          {loadingActivity ? (
            <div className="flex justify-center py-6">
              <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <ActivityTimeline
              entries={recentActivity}
              emptyText="No personal activity recorded yet for today."
            />
          )}
        </CardContent>
      </Card>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Package className="h-4 w-4 text-primary" />
            Stock Check
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Low stock alerts stay visible because they directly affect what you can sell.
          </p>
        </CardHeader>
        <CardContent>
          {filteredInventory.length === 0 ? (
            inventory.length === 0 ? (
              <div className="text-muted-foreground text-sm py-4 text-center space-y-2">
                <p>No items in inventory yet.</p>
                {!effectiveBizId && (
                  <p className="max-w-md mx-auto text-xs">
                    If you expect to see your manager&apos;s stock, your account may not be linked to their business yet.
                    Ask your manager to invite you with your work email; after you accept the invitation and refresh this page, items should appear.
                  </p>
                )}
              </div>
            ) : (
              <NoMatchingRecords />
            )
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 font-semibold">Item Name</th>
                    <th className="text-left py-3 px-2 font-semibold">Category</th>
                    <th className="text-right py-3 px-2 font-semibold">Selling Price</th>
                    <th className="text-right py-3 px-2 font-semibold">Stock Level</th>
                    <th className="text-right py-3 px-2 font-semibold">Status</th>
                    <th className="text-right py-3 px-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.map((item) => {
                    const threshold = item.min_threshold ?? 0;
                    const isLowStock = item.current_stock <= threshold;
                    return (
                      <tr
                        key={item.id}
                        className={`border-b border-border/50 ${
                          isLowStock && notificationPrefs.lowStockAlertsEnabled ? "bg-red-50 dark:bg-red-950/20" : ""
                        }`}
                      >
                        <td className={`py-3 px-2 ${isLowStock ? "font-semibold text-red-600 dark:text-red-400" : ""}`}>
                          {item.name}
                        </td>
                        <td className="py-3 px-2 text-muted-foreground">{item.category || "—"}</td>
                        <td className="py-3 px-2 text-right">
                          {Number(item.selling_price).toFixed(2)}
                        </td>
                        <td className={`py-3 px-2 text-right font-bold ${
                          isLowStock ? "text-red-600 dark:text-red-400" : ""
                        }`}>
                          {item.current_stock}
                        </td>
                        <td className="py-3 px-2 text-right">
                          {isLowStock ? (
                            <Badge variant="destructive" className="text-xs">Low Stock</Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">OK</span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-right">
                          {employeeViewSettings.allowEmployeeAdjustInventory || employeeViewSettings.allowEmployeeRecordSales ? (
                            <DropdownMenu modal={false}>
                              <DropdownMenuTrigger asChild>
                                <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5">
                                  Actions
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44">
                                {employeeViewSettings.allowEmployeeAdjustInventory && (
                                  <DropdownMenuItem onClick={() => openEdit(item)}>
                                    <Pencil className="h-3.5 w-3.5 mr-2" />
                                    Edit Stock
                                  </DropdownMenuItem>
                                )}
                                {employeeViewSettings.allowEmployeeRecordSales && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      navigate("/manual-entry", {
                                        state: {
                                          preselectedItem: {
                                            id: item.id,
                                            name: item.name,
                                            selling_price: item.selling_price,
                                            current_stock: item.current_stock,
                                          } as PreselectedItemForSale,
                                        },
                                      })
                                    }
                                  >
                                    <ShoppingCart className="h-3.5 w-3.5 mr-2" />
                                    Record Sale
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {employeeViewSettings.allowEmployeeAdjustInventory && (
        <Dialog open={!!editItem} onOpenChange={(open) => !open && setEditItem(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Stock</DialogTitle>
            </DialogHeader>
            {editItem && (
              <form onSubmit={handleSaveStock} className="space-y-4">
                <p className="text-sm text-muted-foreground">{editItem.name}</p>
                <div className="space-y-2">
                  <Label>Current stock</Label>
                  <Input
                    type="number"
                    min={0}
                    value={editStock}
                    onChange={(e) => setEditStock(Number(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Low stock threshold</Label>
                  <Input
                    type="number"
                    min={0}
                    value={editThreshold}
                    onChange={(e) => setEditThreshold(Number(e.target.value) || 0)}
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setEditItem(null)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? "Saving…" : "Save"}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      )}

    </div>
  );
}
